import React, { useEffect, useState, useContext } from "react";
import { LinkedInIntegrationsRequestFactory } from "../../../api/requestFactory";
import useRequest from "../../../api/useRequest";
import { useNavigate } from "react-router-dom";
import ConnectAccountImage from "./images/connect-account.svg";
import LogoSymbol from "./images/logo-symbol.svg";
import LinkedInAuth from "../../auth/linkedin-auth";
import LinkedInMigrateAccount from "./linkedin-migrate-account";
import { ERROR_CODES } from "../../../api/errorCodes";
import { NotificationContext } from "../../../context/notification-context";

function LinkedInIntegrations({ setShowLinkedIntegrationModal }) {
  const [linkedInIntegrations, setLinkedInIntegrations] = React.useState([]);
  const { requestMaker } = useRequest();
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [migrateAccountInfo, setMigrateAccountInfo] = useState(null);

  const handleClose = () => {
    if (setShowLinkedIntegrationModal) {
      setShowLinkedIntegrationModal(false);
    } else {
      navigate("/");
    }
  };

  const handleLinkedInAuthClose = (res) => {
    if (
      res?.code === ERROR_CODES.LINKEDIN_MIGRATE_ERROR_ACCOUNT ||
      res?.code === ERROR_CODES.LINKEDIN_MIGRATE_ERROR_WORKSPACE
    ) {
      setMigrateAccountInfo(res);
      setShowMigrateModal(true);
    } else if (res?.errors) {
      let errorContent =
        res?.errors[0] ||
        "Something went wrong while connecting your LinkedIn account. Please try again later.";
      showNotification(
        {
          title: "Error",
          content: errorContent,
        },
        "error"
      );
    } else {
      // Success case - reload page to refresh all state including LinkedIn accounts
      navigate(0);
    }
  };

  useEffect(() => {
    requestMaker(
      LinkedInIntegrationsRequestFactory.createGetLinkedInIntegrationsRequest()
    ).then((response) => {
      setLinkedInIntegrations(response.integrations);
    });
  }, [requestMaker]);

  return (
    <>
      <div className="fixed inset-0 z-40 grid w-screen h-screen p-4 place-items-center bg-gray-700/75 sm:p-6 lg:p-8">
        {linkedInIntegrations.length === 0 && (
          <div className="relative w-full max-w-lg mx-auto overflow-hidden bg-white shadow-xl rounded-xl">
            <div className="px-4 py-6 sm:py-8 sm:px-6 overflow-y-auto max-h-[80vh] lg:max-h-[80vh]">
              <div className="space-y-6">
                <img
                  className="w-64 h-auto mx-auto"
                  src={ConnectAccountImage}
                  alt=""
                />

                <div className="text-center">
                  <p className="mt-2 text-xl font-semibold text-gray-950">
                    Connect LinkedIn Account
                  </p>
                  <p className="max-w-sm mx-auto mt-2 text-base font-normal text-gray-600">
                    Connect your LinkedIn account to schedule and publish post.
                  </p>
                </div>

                <div className="flex flex-col gap-4 sm:justify-center sm:items-center sm:flex-row">
                  <LinkedInAuth onClose={handleLinkedInAuthClose}>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 transition-all duration-150 rounded-full px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 bg-blue-500 text-white"
                    >
                      <svg
                        aria-hidden="true"
                        className="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1zM8.339 18.337H5.667v-8.59h2.672v8.59zM7.003 8.574a1.548 1.548 0 1 1 0-3.096 1.548 1.548 0 0 1 0 3.096zm11.335 9.763h-2.669V14.16c0-.996-.018-2.277-1.388-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248h-2.667v-8.59h2.56v1.174h.037c.355-.675 1.227-1.387 2.524-1.387 2.704 0 3.203 1.778 3.203 4.092v4.71z"></path>
                      </svg>
                      Connect LinkedIn Account
                    </button>
                  </LinkedInAuth>

                  <button
                    onClick={handleClose}
                    type="button"
                    className="inline-flex items-center justify-center gap-2 transition-all duration-150 rounded-full bg-white px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold text-gray-700 shadow-xs ring-1 hover:text-gray-900 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Do It Later
                  </button>
                </div>

                <p className="max-w-sm mx-auto text-xs text-center text-gray-600">
                  You will be redirected to LinkedIn to connect your account.
                </p>
              </div>
            </div>
          </div>
        )}
        {linkedInIntegrations.length > 0 && (
          <div className="relative w-full max-w-lg mx-auto overflow-hidden bg-white shadow-xl rounded-xl">
            <div className="px-4 py-6 sm:py-8 sm:px-6 overflow-y-auto max-h-[80vh] lg:max-h-[80vh]">
              <div className="space-y-6">
                <div className="flex items-center justify-center gap-4 py-8">
                  <div className="relative w-16 h-16 rounded-full shadow-sm sm:w-24 sm:h-24 shrink-0 ring-1 ring-gray-100">
                    <img
                      className="object-cover w-full h-full rounded-full"
                      src={
                        linkedInIntegrations[0].avatar_url ||
                        "https://ckfwvufqcepsjlnnrzqi.supabase.co/storage/v1/object/public/company/logo-placeholder.png"
                      }
                      alt=""
                    />

                    <span className="absolute bottom-0 right-0 w-6 h-6 bg-[#1052B8] inline-flex items-center justify-center rounded-full ring-2 ring-white text-white">
                      <svg
                        aria-hidden="true"
                        className="w-4 h-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <circle cx="4.983" cy="5.009" r="2.188"></circle>
                        <path d="M9.237 8.855v12.139h3.769v-6.003c0-1.584.298-3.118 2.262-3.118 1.937 0 1.961 1.811 1.961 3.218v5.904H21v-6.657c0-3.27-.704-5.783-4.526-5.783-1.835 0-3.065 1.007-3.568 1.96h-.051v-1.66H9.237zm-6.142 0H6.87v12.139H3.095z"></path>
                      </svg>
                    </span>
                  </div>

                  <div className="relative w-32 sm:w-40">
                    <div
                      className="absolute inset-0 flex items-center"
                      aria-hidden="true"
                    >
                      <div className="w-32 border-t-2 border-dashed sm:w-40 border-success-500"></div>
                    </div>

                    <div className="relative flex justify-center">
                      <span className="px-1 bg-white">
                        <svg
                          aria-hidden="true"
                          className="relative z-10 w-8 h-8 text-success-500"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>

                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full shadow-sm sm:h-24 sm:w-24 ring-1 ring-gray-200 shrink-0">
                    <img
                      className="w-auto h-10 sm:h-16"
                      src={LogoSymbol}
                      alt=""
                    />
                  </div>
                </div>

                <div className="text-center">
                  <p className="mt-2 text-xl font-semibold text-gray-950">
                    LinkedIn Account Successfully Connected!
                  </p>
                  <p className="max-w-sm mx-auto mt-2 text-base font-normal text-gray-600">
                    You can now access all the features of Supergrow app
                    seamlessly.
                  </p>
                </div>

                <div className="flex flex-col gap-4 sm:justify-center sm:items-center sm:flex-row">
                  <LinkedInAuth onClose={handleLinkedInAuthClose}>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 transition-all duration-150 rounded-full px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 bg-blue-500 text-white"
                    >
                      Re-Connect LinkedIn Account
                    </button>
                  </LinkedInAuth>

                  <button
                    onClick={handleClose}
                    type="button"
                    className="inline-flex items-center justify-center gap-2 transition-all duration-150 rounded-full px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 bg-blue-500 text-white"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showMigrateModal && migrateAccountInfo && (
        <LinkedInMigrateAccount
          onClose={() => {
            setShowMigrateModal(false);
            setMigrateAccountInfo(null);
          }}
          code={migrateAccountInfo.code}
          name={migrateAccountInfo.profile?.name}
          tempToken={migrateAccountInfo.temp_token}
          avatarUrl={migrateAccountInfo.profile?.avatar_url}
        />
      )}
    </>
  );
}

export default LinkedInIntegrations;
